import React, { useEffect, useState } from "react";
import axiosInstance from "../../utils/axios";
import { toast } from "react-hot-toast";
import Pagination from "../../components/AdminSideComponents/AdminPagination";
import AdminSearchBar from "../../components/AdminSideComponents/AdminSearchBar";
import { useNavigate } from "react-router-dom";

const VolunteerSkeletonLoader = () => {
  return (
    <div className="overflow-x-auto animate-pulse">
      <table className="min-w-full divide-y divide-gray-200 border rounded-lg">
        <thead className="bg-gray-50">
          <tr>
            {["Date", "Name", "Contact", "Status", "Action"].map((col, idx) => (
              <th key={idx} className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {Array.from({ length: 6 }).map((_, idx) => (
            <tr key={idx} className="bg-white">
              {Array.from({ length: 6 }).map((__, i) => (
                <td key={i} className="px-4 py-3 h-10 bg-gray-200 rounded-lg mb-2 border-b-4 border-white"></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const getStatusBadgeClass = (status) => {
  switch (status) {
    case "approved": return "bg-green-100 text-green-800";
    case "rejected": return "bg-red-100 text-red-800";
    default: return "bg-gray-100 text-gray-800"; // submitted
  }
};

const AdminVolunteerApplicationsV2 = () => {
  const ITEMS_PER_PAGE = 8;
  const navigate = useNavigate();

  const [allVolunteers, setAllVolunteers] = useState([]);
  const [filteredVolunteers, setFilteredVolunteers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const getVolunteers = async () => {
    try {
      const resp = await axiosInstance.get("/v2/admin/volunteer/applications");
      const data = resp.data.applications || [];
      setAllVolunteers(data);
      setFilteredVolunteers(data);
      setError(false);
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getVolunteers();
  }, []);

  const onSearchHandler = (query) => {
    const lowerCaseQuery = query.trim().toLowerCase();
    const results = allVolunteers.filter((v) => {
      const name = v.basicInfo?.fullName?.toLowerCase() || "";
      const email = v.basicInfo?.email?.toLowerCase() || "";
      const phone = v.basicInfo?.phone?.toLowerCase() || "";
      return name.includes(lowerCaseQuery) || email.includes(lowerCaseQuery) || phone.includes(lowerCaseQuery);
    });
    setFilteredVolunteers(results);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(filteredVolunteers.length / ITEMS_PER_PAGE);
  const paginatedVolunteers = filteredVolunteers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  return (
    <div>
      <AdminSearchBar
        title="Volunteer Applications V2"
        hideAddButton={true}
        onSearchClick={onSearchHandler}
      />

      <div className="py-6 px-4 md:px-8 overflow-x-auto">
        {loading ? (
          <VolunteerSkeletonLoader />
        ) : error ? (
          <div className="w-full text-center py-16">
            <div className="inline-block bg-red-100 border border-red-300 text-red-700 px-6 py-4 rounded-lg shadow-md">
              <p className="text-lg font-semibold">Oops! Something went wrong.</p>
              <p className="mt-1 text-sm">We couldn't fetch volunteer applications. Please try again later.</p>
            </div>
          </div>
        ) : filteredVolunteers.length === 0 ? (
          <div className="w-full text-center py-10 text-gray-600 text-lg font-medium">
            No volunteer applications found.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 border rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedVolunteers.map((v) => (
                <tr key={v._id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {new Date(v.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {v.basicInfo?.fullName || "Unknown"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    <div>{v.basicInfo?.email}</div>
                    <div className="text-blue-600">{v.basicInfo?.phone}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${getStatusBadgeClass(v.status)}`}>
                      {v.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => navigate(`/admin-volunteer-details/${v._id}`)}
                      className="px-3 py-1.5 bg-primaryColor text-white rounded text-sm hover:bg-teal-800 transition-colors"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Pagination
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        totalPages={totalPages}
      />
    </div>
  );
};

export default AdminVolunteerApplicationsV2;
